import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTranslation } from 'react-i18next'
import { object, SchemaOf, string } from 'yup'
import { User } from 'firebase/auth'
import { Errors } from 'config'
import type { FormProps } from '../Form'
import { useMapTwoFactorError } from 'shared/hooks/use-map-two-factor-error'
import { isTwoFactorEnabled } from 'shared/utils'
import { ReauthenticationStep } from '../../hooks/use-reauthentication'

export enum PasswordFormFields {
  Password = 'password',
}

export interface PasswordFormValues {
  [PasswordFormFields.Password]: string
}

const WRONG_PASSWORD_ERRORS: string[] = [
  Errors.WrongPassword,
  Errors.InvalidCredential,
]

const isWrongPasswordError = (error: string | null) =>
  !!error && WRONG_PASSWORD_ERRORS.includes(error)

// Without 2FA the first factor is the only step, so no progress is shown. A link
// asks for the 2FA code in the tab it is opened in.
export const getStepProgress = (
  step: ReauthenticationStep,
  user: User | null
) => {
  const isTwoStep =
    step !== ReauthenticationStep.EmailLink &&
    (isTwoFactorEnabled(user) || step === ReauthenticationStep.TwoFactorCode)

  return {
    current: step === ReauthenticationStep.TwoFactorCode ? 2 : 1,
    count: isTwoStep ? 2 : 1,
  }
}

// A wrong password marks the field, its message is shown above the form
export const usePasswordFormProps = (
  onSubmit: (password: string) => Promise<void>,
  error: string | null
) => {
  const { t } = useTranslation()
  const schema: SchemaOf<PasswordFormValues> = object().shape({
    [PasswordFormFields.Password]: string().required(t('validation.required')),
  })
  const methods = useForm<PasswordFormValues>({
    defaultValues: { [PasswordFormFields.Password]: '' },
    resolver: yupResolver(schema),
  })
  const { setError } = methods

  useEffect(() => {
    if (isWrongPasswordError(error))
      setError(PasswordFormFields.Password, { message: '' })
  }, [error, setError])

  const formProps: FormProps<PasswordFormValues> = {
    ...methods,
    onSubmit: values => onSubmit(values[PasswordFormFields.Password]),
  }

  return formProps
}

// Errors of the first factor. The 2FA mapping covers the ones common to every
// step, e.g. too many requests or being offline.
export const useMapReauthenticationError = () => {
  const { t } = useTranslation()
  const { mapError: mapTwoFactorError } = useMapTwoFactorError()

  const mapError = (error: string) => {
    if (isWrongPasswordError(error)) return t('reauthentication.wrongPassword')
    if (error === Errors.PasswordDoesNotMeetRequirements)
      return t('passwordSafetyWarning')
    return mapTwoFactorError(error)
  }

  return { mapError }
}
